import React, { useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { productionCosts, costDetails } from '../../data/costAccountingData';

const ProductionCostsView = () => {
  const [selectedCost, setSelectedCost] = useState(null);

  const handleCostClick = (costId) => {
    const cost = productionCosts.find(c => c.production_cost_id === costId);
    setSelectedCost(cost);
  };

  const getCostDetails = (costId) => {
    return costDetails.filter(detail => detail.production_cost_id === costId);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">生產成本</h2>
        <p className="text-gray-600">Production Costs</p>
      </div>

      {/* Production Costs header image */}
      <div className="relative h-48 mb-6 overflow-hidden rounded-lg shadow-md">
        <img 
          src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
          alt="Production Costs" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <h1 className="text-white text-3xl font-bold">生產成本分析</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">生產成本清單</h3>
            <div className="space-y-2">
              {productionCosts.map((cost) => (
                <div 
                  key={cost.production_cost_id} 
                  className={`p-4 border rounded-md cursor-pointer transition-colors ${selectedCost && selectedCost.production_cost_id === cost.production_cost_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => handleCostClick(cost.production_cost_id)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">{cost.product_name}</p>
                      <p className="text-sm text-gray-500">訂單: {cost.production_order_id}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${cost.status === 'FINALIZED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {cost.status === 'FINALIZED' ? '已確認' : '草稿'}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">數量: {cost.quantity_produced}</p>
                      <p className="text-gray-500">期間: {cost.cost_period}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">總成本: ${cost.total_production_cost.toLocaleString()}</p>
                      <p className="text-gray-500">單位成本: ${cost.unit_cost.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedCost ? (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">{selectedCost.product_name} 成本詳情</h3>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${selectedCost.status === 'FINALIZED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {selectedCost.status === 'FINALIZED' ? '已確認' : '草稿'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">直接材料</p>
                  <p className="text-xl font-semibold text-gray-900">${selectedCost.direct_material_cost.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">單位: ${(selectedCost.direct_material_cost / selectedCost.quantity_produced).toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">直接人工</p>
                  <p className="text-xl font-semibold text-gray-900">${selectedCost.direct_labor_cost.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">單位: ${(selectedCost.direct_labor_cost / selectedCost.quantity_produced).toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">製造費用</p>
                  <p className="text-xl font-semibold text-gray-900">${selectedCost.manufacturing_overhead.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">單位: ${(selectedCost.manufacturing_overhead / selectedCost.quantity_produced).toFixed(2)}</p>
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg mb-6">
                <div>
                  <p className="text-sm text-blue-700">總生產成本</p>
                  <p className="text-2xl font-bold text-blue-900">${selectedCost.total_production_cost.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-700">單位成本</p>
                  <p className="text-2xl font-bold text-blue-900">${selectedCost.unit_cost.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-700">標準成本</p>
                  <p className="text-2xl font-bold text-blue-900">${selectedCost.standard_cost.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-700">成本差異</p>
                  <p className={`text-2xl font-bold ${selectedCost.cost_variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedCost.cost_variance > 0 ? '+' : ''}{selectedCost.cost_variance.toFixed(2)} ({selectedCost.variance_percentage.toFixed(2)}%)
                  </p>
                </div>
              </div>

              <h4 className="text-lg font-semibold text-gray-800 mb-4">成本明細</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">成本要素</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">成本中心</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單位</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單價</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">總金額</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getCostDetails(selectedCost.production_cost_id).map((detail) => (
                      <tr key={detail.detail_id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{detail.cost_element_name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{detail.cost_center_name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{detail.quantity}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{detail.unit_of_measure}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">${detail.unit_cost.toFixed(2)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">${detail.total_cost.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="5" className="px-4 py-3 text-right text-sm font-medium text-gray-900">總計:</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        ${getCostDetails(selectedCost.production_cost_id).reduce((sum, detail) => sum + detail.total_cost, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">備註:</p>
                <p className="text-sm text-gray-600">{selectedCost.notes}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center h-full">
              <ClipboardList className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-lg text-gray-500">選擇左側的生產成本項目查看詳情</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionCostsView;
